// VirtualPatchProfiles.tsx
import { useState, useCallback, useEffect } from "react";
import {
    RefreshCw, Loader2, Search, ShieldCheck,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown
} from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";

// ── Types ─────────────────────────────────────────────────────────────────────
interface VirtualPatchProfile {
    name: string;
    log: string;
    action: string;
    comment: string;
    severity: string;
    exemption: any[];
    q_origin_key: string;
}

// ── Value Chip ────────────────────────────────────────────────────────────────
const ValChip = ({ val }: { val: string | undefined }) => {
    const str = String(val ?? "");
    if (!str) return <span className="text-slate-400 dark:text-gray-500 text-[9px]">—</span>;
    const lower = str.toLowerCase();
    const style =
        lower === "enable" || lower === "block"
            ? "bg-green-500/10 border-green-500/20 text-green-600 dark:text-green-400"
            : lower === "disable"
                ? "bg-slate-100 dark:bg-gray-700/40 border-slate-200 dark:border-gray-600/30 text-slate-500 dark:text-gray-400"
                : lower === "monitor" || lower === "pass"
                    ? "bg-cyan-500/10 border-cyan-500/20 text-cyan-600 dark:text-cyan-400"
                    : "bg-indigo-500/10 border-indigo-500/20 text-indigo-600 dark:text-indigo-400";
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold border uppercase whitespace-nowrap ${style}`}>
            {str}
        </span>
    );
};

// ── Severity Chips ────────────────────────────────────────────────────────────
const SEVERITY_STYLES: Record<string, string> = {
    info: "bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400",
    low: "bg-cyan-500/10 border-cyan-500/20 text-cyan-600 dark:text-cyan-400",
    medium: "bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-yellow-400",
    high: "bg-orange-500/10 border-orange-500/20 text-orange-600 dark:text-orange-400",
    critical: "bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-red-400",
};

const SeverityChips = ({ val }: { val: string }) => {
    if (!val) return <span className="text-slate-400 dark:text-gray-500 text-[9px]">—</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {val.split(" ").filter(Boolean).map(s => (
                <span key={s} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold border uppercase ${SEVERITY_STYLES[s.toLowerCase()] ?? "bg-slate-100 dark:bg-gray-600/20 border-slate-200 dark:border-gray-600/20 text-slate-600 dark:text-gray-300"}`}>
                    {s}
                </span>
            ))}
        </div>
    );
};

// ── Sort Icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ col, sort }: { col: string; sort: { key: string; dir: "asc" | "desc" } }) => {
    if (sort.key !== col) return <ArrowUp size={9} className="opacity-0 group-hover:opacity-30 text-slate-400 dark:text-gray-400 transition-opacity" />;
    return sort.dir === "asc"
        ? <ArrowUp size={9} className="text-emerald-600 dark:text-emerald-400" />
        : <ArrowDown size={9} className="text-emerald-600 dark:text-emerald-400" />;
};

// ── Table Columns ─────────────────────────────────────────────────────────────
const COLS: { key: string; label: string; width: string; render: (p: VirtualPatchProfile) => React.ReactNode }[] = [
    {
        key: "name", label: "Profile Name", width: "min-w-[140px]",
        render: p => <p className="text-[11px] font-black text-slate-900 dark:text-white">{p.name}</p>,
    },
    {
        key: "action", label: "Action", width: "w-20",
        render: p => <ValChip val={p.action} />,
    },
    {
        key: "log", label: "Log", width: "w-16",
        render: p => <ValChip val={p.log} />,
    },
    {
        key: "severity", label: "Severity", width: "min-w-[220px]",
        render: p => <SeverityChips val={p.severity} />,
    },
    {
        key: "_exemptionCount", label: "Exemptions", width: "w-24",
        render: p => (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black border ${p.exemption.length > 0
                ? "bg-orange-500/10 border-orange-500/20 text-orange-600 dark:text-orange-400"
                : "bg-slate-100 dark:bg-gray-700/40 border-slate-200 dark:border-gray-600/30 text-slate-500 dark:text-gray-400"
                }`}>
                {p.exemption.length === 0 ? "None" : p.exemption.length}
            </span>
        ),
    },
    {
        key: "comment", label: "Comment", width: "min-w-[160px]",
        render: p => (
            <p className="text-[10px] text-slate-500 dark:text-gray-400 truncate max-w-[200px]" title={p.comment}>
                {p.comment || "—"}
            </p>
        ),
    },
];

// ── Main Component ────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 20, 50];

export default function VirtualPatchProfiles({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [profiles, setProfiles] = useState<VirtualPatchProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "name", dir: "asc" });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: "virtual_patch_profiles",
                    }),
                    limit: 1,
                },
            });
            if (res.data.status && res.data.data?.[0]) {
                const raw = res.data.data[0].results;
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                setProfiles(parsed.items ?? []);
            } else showError("Failed to load virtual patch profiles");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, client_id]);

    useEffect(() => { fetchData(); }, [fetchData]);
    useEffect(() => { setPage(1); }, [search]);

    const filtered = profiles.filter(p => {
        if (!search) return true;
        const q = search.toLowerCase();
        return p.name.toLowerCase().includes(q) || p.comment?.toLowerCase().includes(q);
    });

    const sorted = [...filtered].sort((a, b) => {
        let av: any, bv: any;
        if (sort.key === "_exemptionCount") { av = a.exemption.length; bv = b.exemption.length; }
        else { av = (a as any)[sort.key]; bv = (b as any)[sort.key]; }
        const cmp = typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av ?? "").localeCompare(String(bv ?? ""));
        return sort.dir === "asc" ? cmp : -cmp;
    });

    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageItems = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);
    const start = sorted.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const end = Math.min(safePage * pageSize, sorted.length);

    const handleSort = (key: string) => {
        setSort(prev => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
        setPage(1);
    };

    return (
        <div className="flex flex-col gap-3">

            {/* Filter Bar */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative group">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 dark:text-gray-400 group-focus-within:text-emerald-600 dark:group-focus-within:text-emerald-400 transition-colors" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search profile name..."
                        className="w-56 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg pl-7 pr-3 py-1.5 text-[11px] text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-gray-600 focus:outline-none focus:border-emerald-500/50 transition-all shadow-sm dark:shadow-none"
                    />
                </div>
                {search && (
                    <button onClick={() => setSearch("")}
                        className="text-[9px] text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-bold uppercase tracking-widest">
                        Clear
                    </button>
                )}
                <button
                    onClick={fetchData}
                    disabled={loading}
                    className="ml-auto p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-gray-700 transition-all group shadow-sm dark:shadow-none"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-emerald-600 dark:group-hover:text-emerald-400"} />
                </button>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-20">
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-600 dark:text-emerald-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-400">Loading virtual patch profiles...</span>
                </div>
            ) : sorted.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-16 opacity-40">
                    <ShieldCheck size={22} className="text-slate-400 dark:text-gray-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-400">No profiles found</span>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-none">
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-slate-200 dark:border-gray-700/60">
                                    {COLS.map(col => (
                                        <th
                                            key={col.key}
                                            onClick={() => handleSort(col.key)}
                                            className={`px-3 py-2 text-[9px] font-bold text-slate-700 dark:text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group select-none ${col.width}`}
                                        >
                                            <div className="flex items-center gap-1">
                                                {col.label}
                                                <SortIcon col={col.key} sort={sort} />
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-gray-700/30">
                                {pageItems.map(profile => (
                                    <tr key={profile.name} className="hover:bg-slate-50 dark:hover:bg-gray-700/20 transition-colors">
                                        {COLS.map(col => (
                                            <td key={col.key} className="px-3 py-2.5 align-middle">
                                                {col.render(profile)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 dark:border-gray-700/60 bg-slate-50 dark:bg-gray-800/40 flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[9px] text-slate-500 dark:text-gray-500 uppercase tracking-widest">Rows</span>
                                <select
                                    value={pageSize}
                                    onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1.5 py-0.5 text-[10px] text-slate-800 dark:text-gray-300 focus:outline-none shadow-sm dark:shadow-none"
                                >
                                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                            <span className="text-[9px] text-slate-500 dark:text-gray-400">
                                <span className="text-slate-700 dark:text-gray-300 font-semibold">{start}</span>{" – "}
                                <span className="text-slate-700 dark:text-gray-300 font-semibold">{end}</span>
                                <span className="mx-1 text-slate-400 dark:text-gray-600">/</span>
                                <span className="text-slate-700 dark:text-gray-300 font-bold">{sorted.length}</span>
                                {sorted.length !== profiles.length && (
                                    <span className="text-slate-400 dark:text-gray-500 ml-1">(filtered from {profiles.length})</span>
                                )}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button disabled={safePage === 1} onClick={() => setPage(1)}
                                className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                                <ChevronsLeft size={13} />
                            </button>
                            <button disabled={safePage === 1} onClick={() => setPage(p => p - 1)}
                                className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                                <ChevronLeft size={13} />
                            </button>
                            <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded text-[10px] font-bold mx-1 min-w-[28px] text-center">
                                {safePage}
                            </span>
                            <span className="text-[9px] text-slate-500 dark:text-gray-500">/ {totalPages}</span>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}
                                className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                                <ChevronRight size={13} />
                            </button>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}
                                className="p-1 text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 rounded border border-slate-200 dark:border-transparent transition-all">
                                <ChevronsRight size={13} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
